import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import { Globe, ChevronRight, Lightbulb } from "lucide-react";
import ProgressCard from "./ProgressCard";
import { CampaignStats } from "./types";
import DonationNavabar from "./DonationNavabar";

type Props = {
  stats: CampaignStats;
  onOpenStory?: () => void;
};

const Hero: React.FC<Props> = ({ stats, onOpenStory }) => {
  const theme = useTheme();
  const themeState = useSelector((s: RootState) => s.theme);
  const colors = tokens(theme.palette.mode, themeState);

  const scrollToDonate = () => {
    document
      .getElementById("donate-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Box component="header" sx={{ position: "relative", overflow: "hidden" }}>
      {/* Decorative angled background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: { xs: "100%", md: "35%" },
          height: "100%",
          bgcolor: `${colors.primary[400]}10`,
          transform: { md: "skewX(12deg) translateX(96px)" },
          transformOrigin: "top",
          opacity: 0.4,
          zIndex: 0,
        }}
      />

      <DonationNavabar />

      {/* ================= HERO CONTENT ================= */}
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          px: 3,
          py: { xs: 8, md: 14 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 520px" },
          gap: 6,
          alignItems: "center",
          mt: 0,
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* LEFT SIDE */}
        <Box>
          <Chip
            icon={<Globe size={16} />}
            label="Powering Churches Across Africa"
            sx={{
              bgcolor: `${colors.primary[400]}15`,
              color: colors.primary[400],
              fontWeight: 700,
              mb: 2,
            }}
          />

          <Typography variant="h2" sx={{ fontWeight: 900, mt: 2 }}>
            Turn{" "}
            <Box component="span" sx={{ color: colors.primary[400] }}>
              Sunshine
            </Box>{" "}
            Into{" "}
            <Box component="span" sx={{ color: colors.secondary[300] }}>
              Sermons
            </Box>
            .
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "text.secondary", mt: 2, maxWidth: 600 }}
          >
            Many rural churches in Africa lose power after sunset, limiting
            community outreach and night schools. Help us provide sustainable
            solar energy to the heart of the community.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              endIcon={<ChevronRight />}
              sx={{
                bgcolor: colors.primary[400],
                color: colors.primary[900],
                borderRadius: 3,
                px: 4,
              }}
              onClick={scrollToDonate}
            >
              Start Donating
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<Lightbulb />}
              onClick={() => onOpenStory?.()}
              sx={{ borderRadius: 3, px: 4 }}
            >
              Pastors: Start Your Campaign
            </Button>
          </Stack>

          {/* AvatarGroup + dynamic donor count (NEW) */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4 }}>
            <AvatarGroup max={4}>
              {[1, 2, 3, 4].map((i) => (
                <Avatar
                  key={i}
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=user${i}`}
                  sx={{ width: 40, height: 40 }}
                />
              ))}
            </AvatarGroup>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <strong>{stats.donorCount}+</strong> partners have already joined
              this light mission.
            </Typography>
          </Stack>
        </Box>

        {/* RIGHT SIDE - ProgressCard now receives stats */}
        <ProgressCard stats={stats} />
      </Box>
    </Box>
  );
};

export default Hero;
